import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Gift } from "lucide-react";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [referralCode, setReferralCode] = useState(params.get("ref") || "");
  const [loading, setLoading] = useState(false);

  // Log the referral visit exactly once per referral code per browser so we can nudge the referrer if the visitor bounces.
  useEffect(() => {
    const code = params.get("ref");
    if (!code) return;
    const upper = code.trim().toUpperCase();
    const key = `aiapply_ref_visit_${upper}`;
    if (localStorage.getItem(key)) return;
    api.post("/referrals/visit", { referral_code: upper })
      .then(() => localStorage.setItem(key, new Date().toISOString()))
      .catch(() => { /* silent */ });
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    setLoading(true);
    const res = await register(name, email, password, referralCode || null);
    setLoading(false);
    if (res.ok) { toast.success(referralCode ? "Account created — welcome!" : "Account created"); nav("/app"); }
    else toast.error(res.error || "Sign up failed");
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="flex items-center justify-center p-8 bg-[#FDFDFB] order-2 md:order-1">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-10">
            <Logo />
          </Link>
          <div className="overline">Get started</div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-2">Create your account</h1>
          <p className="text-sm text-[#5C6661] mt-2">Free — 5 matched companies to try before you decide.</p>
          <form onSubmit={submit} className="mt-8 space-y-5">
            <div><Label htmlFor="name">Full name</Label>
              <Input id="name" data-testid="register-name" required value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 h-11 rounded-lg" /></div>
            <div><Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="register-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-11 rounded-lg" /></div>
            <div><Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="register-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 h-11 rounded-lg" /></div>
            <div>
              <Label htmlFor="ref" className="flex items-center gap-1.5"><Gift className="w-3 h-3 text-[#2D4A3E]" /> Referral code <span className="text-[#5C6661] font-normal">(optional)</span></Label>
              <Input id="ref" data-testid="register-referral" value={referralCode} onChange={(e) => setReferralCode(e.target.value.toUpperCase())} placeholder="e.g. A7BX2K" className="mt-1.5 h-11 rounded-lg font-mono" />
              {referralCode && <div className="text-xs text-[#2D4A3E] mt-1.5">Your friend gets a free month of Pro when you upgrade.</div>}
            </div>
            <Button type="submit" data-testid="register-submit" disabled={loading} className="w-full h-11 rounded-full bg-[#2D4A3E] hover:bg-[#3B5F50]">{loading ? "Creating…" : "Create account"}</Button>
          </form>
          <div className="mt-6 text-sm text-[#5C6661]">
            Already have one? <Link to="/login" data-testid="link-login" className="text-[#2D4A3E] font-medium hover:underline">Sign in</Link>
          </div>
          <div className="mt-6 text-xs text-[#5C6661]">By continuing you agree to our <Link to="/terms" className="underline">Terms</Link> and <Link to="/privacy" className="underline">Privacy Policy</Link>.</div>
        </div>
      </div>
      <div className="hidden md:block relative order-1 md:order-2">
        <img src="https://images.unsplash.com/photo-1586769852044-692d6e3703f0?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200" alt="Envelopes on a wooden desk" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.src = "https://images.unsplash.com/photo-1455390582262-044cdead277a?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200"; }} />
        <div className="absolute inset-0 bg-gradient-to-bl from-[#2D4A3E]/40 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <h2 className="font-display text-3xl font-bold tracking-tight">A better first impression.</h2>
          <p className="text-sm opacity-80 mt-2">Personal, well-crafted applications — sent straight from your own inbox.</p>
        </div>
      </div>
    </div>
  );
}
